import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { OfflineModeService } from '@luzula/luzula-lib';

/**
 * Laedt eine SEA95-Originalseite aus den statischen bpweb-Assets. Wird in der
 * Bodensteckbrief-Preview und im Editor optional dazugeschaltet, wenn der
 * Admin "Original anzeigen" anklickt. Die Komponente erscheint nicht im DOM,
 * solange der Toggle aus ist - es laeuft also kein HTTP-Request, bevor der
 * Admin das ausdruecklich wuenscht.
 *
 * Quelle: `assets/SEA/originals/<pdf>/page_<N>.webp`. Liegt im selben Asset-
 * Ordner wie die SEA-Teile A-D-PDFs (Handbuch). Im Service-Worker als lazy-
 * cache-Gruppe `sea-originals` registriert (ngsw-config.json) - kein Precache,
 * nur on-demand, danach offline verfuegbar.
 *
 * Verfuegbarkeitscheck per HEAD: wenn das Bild nicht existiert (404), zeigt
 * die Komponente einen Hinweis, statt ein gebrochenes <img> zu rendern.
 * `loading="lazy"` plus immutable-Cache-Header vom Backend sorgen dafuer,
 * dass Mehrfach-Aufrufe derselben Seite den Browser-Cache treffen.
 *
 * Offline: Da die Scans nur lazy gecacht werden, sind sie ohne vorherigen
 * Download nicht verfuegbar. In dem Fall (kein Netz bzw. Offline-Modus) zeigt
 * die Komponente einen ehrlichen Hinweis statt eines Ladefehlers - inkl. dem
 * Tipp, beim Offline-Stellen die Option „SEA-Dokumente" anzuhaken (die laedt
 * PDFs + Original-Scans in einem Rutsch).
 */
@Component({
  standalone: false,
  selector: 'app-sea-original',
  template: `
    <div class="card border-secondary">
      <div class="card-header py-2 d-flex align-items-center gap-2">
        <i class="bi bi-file-image"></i>
        <strong class="small">Original</strong>
        <small class="text-muted">
          @if (pdf) { {{ pdf }} · S.{{ page }} }
        </small>
      </div>
      <div class="card-body p-2">
        @if (state === 'checking') {
          <div class="text-muted small fst-italic">Prüfe Verfügbarkeit…</div>
        } @else if (state === 'missing') {
          <div class="text-muted small fst-italic">
            Kein Original verfügbar.
            <br>
            <span class="text-secondary">
              Asset fehlt unter
              <code>assets/SEA/originals/{{ pdf }}/page_{{ page }}.webp</code>.
            </span>
          </div>
        } @else if (state === 'offline') {
          <div class="text-muted small">
            <i class="bi bi-wifi-off me-1"></i>
            Original offline nicht verfügbar.
            <br>
            <span class="text-secondary">
              Bitte vorher online öffnen – oder beim Offline-Stellen die Option
              „SEA-Dokumente" anhaken, dann sind die Scans offline dabei.
            </span>
          </div>
        } @else if (state === 'error') {
          <div class="text-warning small">
            Original konnte nicht geladen werden.
          </div>
        } @else if (state === 'available' && imageUrl) {
          <img [src]="imageUrl" loading="lazy" alt="SEA95-Originalseite"
               class="sea-original-img">
        }
      </div>
    </div>
  `,
  styles: [`
    .sea-original-img {
      display: block;
      width: 100%;
      height: auto;
      max-height: calc(100vh - 200px);
      object-fit: contain;
      background: #fafafa;
    }
  `],
})
export class SeaOriginalComponent implements OnChanges {
  @Input() pdf: string | undefined;
  @Input() page: number | undefined;

  /** Optional injiziert (in reinen Unit-Tests ohne DI ist er undefined ->
   *  isOffline() liefert dann false und der bisherige Online-Pfad greift). */
  private oms = inject(OfflineModeService, { optional: true });

  state: 'idle' | 'checking' | 'available' | 'missing' | 'error' | 'offline' = 'idle';
  imageUrl = '';

  /** True, wenn der Nutzer im Offline-Modus ist oder kein Netz erreichbar ist -
   *  dann bedeutet ein fehlgeschlagener Abruf „nicht vorab geladen", nicht
   *  „Asset fehlt"/„Serverfehler". */
  private isOffline(): boolean {
    return !!this.oms && (this.oms.offlineMode() || !this.oms.networkOnline());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pdf'] || changes['page']) this.check();
  }

  /** GET-Abfrage gegen das Asset (bewusst KEIN HEAD): Der Angular-Service-Worker
   *  bedient nur GET aus dem Cache — seine `cacheQueryOptions` setzen kein
   *  `ignoreMethod`, deshalb matcht ein HEAD-Request offline nie den gecachten
   *  GET-Eintrag und der Scan bliebe offline unsichtbar, obwohl er (vorab)
   *  geladen wurde. Der GET-Probe füllt zudem den SW-Cache, sodass das folgende
   *  <img> denselben Eintrag trifft (kein zweiter Netz-Download). Fehlt die Datei
   *  online -> 404 -> "missing"; offline und nicht geladen -> "offline". */
  private async check(): Promise<void> {
    if (!this.pdf || this.page === undefined || this.page === null) {
      this.state = 'idle';
      this.imageUrl = '';
      return;
    }
    const url = `assets/SEA/originals/${encodeURIComponent(this.pdf)}/page_${this.page}.webp`;
    this.state = 'checking';
    this.imageUrl = '';
    try {
      const res = await fetch(url);
      if (res.ok) {
        this.imageUrl = url;
        this.state = 'available';
      } else if (this.isOffline()) {
        // Offline und nicht (vorab) gecacht -> ehrlicher Offline-Hinweis.
        this.state = 'offline';
      } else if (res.status === 404) {
        this.state = 'missing';
      } else {
        this.state = 'error';
      }
    } catch {
      // Abruf fehlgeschlagen: offline = nicht geladen, sonst echter Fehler.
      this.state = this.isOffline() ? 'offline' : 'error';
    }
  }
}
